import axiosInstance from './axiosInstance';

const BASE = '/vehicle-service-records';

/**
 * Fetch a paginated list of vehicle service records.
 *
 * Searches the organisation's whole history — there is no date range. The
 * backend requires at least one text filter (rego, name or mobile); the page is
 * responsible for collecting one before calling this function — no validation
 * happens here.
 *
 * @param {object} params
 * @param {string} [params.rego]    Partial rego, case-insensitive
 * @param {string} [params.name]    Partial customer name, case-insensitive
 * @param {string} [params.mobile]  Partial customer mobile (literal substring)
 * @param {number} [params.page=0]  Zero-based page index
 * @param {number} [params.size=20] Page size
 */
export const getVehicleServiceRecords = ({
                                             rego = '',
                                             name = '',
                                             mobile = '',
                                             page = 0,
                                             size = 20,
                                         } = {}) => {
    const params = { page, size };
    if (rego && rego.trim())     params.rego   = rego.trim().toUpperCase();
    if (name && name.trim())     params.name   = name.trim();
    if (mobile && mobile.trim()) params.mobile = mobile.trim();
    return axiosInstance.get(BASE, { params });
};

/**
 * Fetch the full service history for a specific vehicle service record by id.
 *
 * @param {number} id - VehicleServiceRecord PK
 */
export const getVehicleServiceRecordById = (id) =>
    axiosInstance.get(`${BASE}/${id}`);

/**
 * Fetch the most recent service records for a single vehicle, sorted by
 * service completion date descending. No date range — returns the last
 * {@code limit} services regardless of when they occurred.
 *
 * Used by the booking form's "View history" slide-out to preview prior
 * services for the selected vehicle. Returns a plain array (not a Page).
 *
 * @param {string} vehicleUuid - UUID of the vehicle
 * @param {number} [limit=3]   - max records to return, clamped to 1..20 server-side
 * @returns {Promise} resolves to an axios response whose data is an array of
 *                    ServiceRecordSummary objects (same shape as list endpoint
 *                    rows). May be empty when the vehicle has no completed
 *                    services yet.
 */
export const getRecentServiceRecordsByVehicle = (vehicleUuid, limit = 3) =>
    axiosInstance.get(`${BASE}/by-vehicle/${encodeURIComponent(vehicleUuid)}`, {
        params: { limit },
    });

/**
 * Get a pre-signed S3 URL for a note image using its S3 key.
 *
 * Used for images in vehicle service record snapshots where the original
 * JobNoteImage DB row may have been pruned by the nightly DataCleanupJob.
 * The S3 object itself is never deleted so the key stored in NoteImageSnapshot.s3Key
 * at journal-extraction time is always valid.
 *
 * @param {string} s3Key - full S3 key, e.g. job/{jobUuid}/notes/{noteUuid}/{imageUuid}.jpg
 * @returns {Promise} resolves to { key, url }
 */
export const getServiceRecordImageUrl = (s3Key) =>
    axiosInstance.get(`${BASE}/image-url`, { params: { key: s3Key } });

/**
 * Get a pre-signed S3 URL for an invoice PDF using its S3 key.
 *
 * Used for invoice PDFs in vehicle service record snapshots where the original
 * Invoice DB row may have been pruned by the nightly DataCleanupJob.
 * The S3 object itself is never deleted so the key stored in
 * VehicleServiceJobEntry.invoicePdfS3Key at journal-extraction time is always valid.
 *
 * @param {string} s3Key - full S3 key, e.g. invoices/{orgUuid}/{invoiceNumber}.pdf
 * @returns {Promise} resolves to { key, url }
 */
export const getServiceRecordInvoiceUrl = (s3Key) =>
    axiosInstance.get(`${BASE}/invoice-url`, { params: { key: s3Key } });